/*
  Invoicing tab › QuickBooks — READ ONLY (owner decision 9 Oct 2026; src/lib/qbDesktop.js, backend/routes/qb_desktop.py).
  The company's books stay in QuickBooks Desktop; the Web Connector copies them in and this panel shows the project's
  QuickBooks side NEXT TO the platform's own numbers — nothing here is overwritten, nothing is written to QuickBooks.
  (Not the older QuickBooks Online "Sync to QuickBooks" of this tab: that one is QuickBooksChip / QuickBooksSyncNote.)
  - Not linked: "Link this project to its QuickBooks customer / job" with the suggestions by name (best first) and a
    search over every customer / job. Link only for people who can edit Invoicing (the server refuses the others);
    they see the suggestions as text.
  - Linked: "QuickBooks · Client:Job" with Change / Unlink, four figures (Invoiced, Received, Open balance, Costs) and
    folding sections: QuickBooks invoices (with the invoice here each one matches, or "Only in QuickBooks"),
    payments received, costs (by vendor, then the latest lines) and open purchase orders.
  Linking / unlinking is one undoable change: the toast's Undo posts /undo/entries/{undo_id}.

  useQbDesktopProject(projectId, { enabled }) → { data, reload }: GET /qb-desktop/status, then
  /qb-desktop/projects/:id once the tab has been opened. data stays null while the integration is off, has not read
  QuickBooks yet or a request fails — the tab is then exactly as before.
  qbProjectMatchFor(data) → (invoice) → its match ('invoice:<id>' / 'variation_invoice:<id>') for the row chips, or
  null when there is nothing to compare (not linked).
*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeftRight, ChevronRight, Link2, Search, Unlink } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime, formatNumber, formatTimeAgo } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { fetchQbCustomers, fetchQbProject, fetchQbStatus, linkQbCustomer, qbDataReady, qbMatchTone } from '@/lib/qbDesktop';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { notify } from '@/components/ui/notify';
import { SearchSelect } from '@/components/supply-finance/SearchSelect';
import { qbMatchKey, qbMatchTitle } from '@/components/supply-finance/QbDesktopMatch';
import { MONO, MiniStat, Panel, PanelHead, ProgressBar } from './ProjectUi';

const money = (value) => formatNumber(Number(value || 0), { decimals: 2 });
const ROW_LIMIT = 8;
const COST_KIND_LABELS = { bill: 'Bill', check: 'Check', card_charge: 'Card charge' };
const COST_KIND_PLURALS = { bill: 'Bills', check: 'Checks', card_charge: 'Card charges' };

/** The project's QuickBooks side, read once the Invoicing tab is opened (see the header). */
export function useQbDesktopProject(projectId, { enabled = false } = {}) {
  const [requested, setRequested] = useState(false);
  const [state, setState] = useState({ projectId: '', data: null });
  const [tick, setTick] = useState(0);
  useEffect(() => { if (enabled) setRequested(true); }, [enabled]);
  useEffect(() => {
    if (!requested || !projectId) return undefined;
    let alive = true;
    fetchQbStatus()
      .then((status) => (qbDataReady(status) ? fetchQbProject(projectId) : null))
      .then((result) => { if (alive) setState({ projectId, data: result && typeof result === 'object' ? result : null }); })
      // an extra next to the platform's numbers: a failed read leaves the tab as it was
      .catch(() => {});
    return () => { alive = false; };
  }, [requested, projectId, tick]);
  const reload = useCallback(() => setTick((value) => value + 1), []);
  return { data: state.projectId === projectId ? state.data : null, reload };
}

/** invoice → its QuickBooks match (base: 'invoice:<id>', VO: 'variation_invoice:<id>'); null without matches. */
export function qbProjectMatchFor(data) {
  const matches = data?.matches;
  if (!matches || typeof matches !== 'object') return null;
  return (invoice) => (invoice?.id ? matches[qbMatchKey(invoice.id, Boolean(invoice.variation_id))] || null : null);
}

/** A searchable list of every QuickBooks customer / job (GET /qb-desktop/customers, read when first opened). */
function CustomerPicker({ trigger, onPick }) {
  const cache = useRef(null);
  const [failed, setFailed] = useState(false);
  const loadOptions = useCallback(async (query) => {
    if (!cache.current) cache.current = fetchQbCustomers().then((res) => res?.customers || []);
    let customers = [];
    try {
      customers = await cache.current;
      setFailed(false);
    } catch {
      cache.current = null; // read again the next time the list opens
      setFailed(true);
      return [];
    }
    const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
    return customers
      .filter((customer) => words.every((word) => String(customer.full_name || '').toLowerCase().includes(word)))
      .slice(0, 50)
      .map((customer) => ({
        value: customer.qb_id,
        label: customer.full_name || customer.name || customer.qb_id,
        sub: customer.is_active === false ? 'Inactive in QuickBooks' : undefined,
      }));
  }, []);
  return (
    <SearchSelect
      value=""
      onChange={(value, option) => onPick(value, option?.label || '')}
      loadOptions={loadOptions}
      searchPlaceholder="Search QuickBooks customers and jobs"
      emptyText={failed ? 'Could not load the QuickBooks customers' : 'No customer or job matches'}
      align="end"
      popoverClassName="w-[340px]"
      trigger={trigger}
    />
  );
}

function Section({ id, title, count, amount, open, onToggle, children }) {
  const bodyId = `qbd-section-${id}-body`;
  return (
    <section className="border-t border-line-2" data-testid={`qbd-section-${id}`}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? bodyId : undefined}
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-5"
      >
        <ChevronRight className={cn('h-4 w-4 shrink-0 text-ink-3 transition-transform', open && 'rotate-90')} aria-hidden="true" />
        <span className="text-[13px] font-bold text-ink">{title}</span>
        <span className="text-[12px] tabular-nums text-ink-3">{formatNumber(count)}</span>
        {amount !== undefined ? <span className="ml-auto text-[12.5px] font-semibold tabular-nums text-ink">{money(amount)}</span> : null}
      </button>
      {open ? <div id={bodyId} className="pb-3" data-testid={`qbd-section-${id}-body`}>{children}</div> : null}
    </section>
  );
}

const head = 'h-8 whitespace-nowrap border-b border-line bg-surface-2 px-3 text-left text-[11.5px] font-bold text-ink-3 first:pl-4 last:pr-4 sm:first:pl-5 sm:last:pr-5';
const cell = 'border-b border-line-2 px-3 py-1.5 align-middle text-[12.5px] first:pl-4 last:pr-4 sm:first:pl-5 sm:last:pr-5';
const dash = <span className="text-ink-4">—</span>;

function MiniTable({ minWidth = 560, columns, children }) {
  return (
    <div className="relative overflow-x-auto">
      <table className="w-full border-separate border-spacing-0" style={{ minWidth }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.label} className={cn(head, column.right && 'text-right')}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Empty({ children }) {
  return <p className="px-4 py-1 text-[12.5px] text-ink-3 sm:px-5">{children}</p>;
}

function MoreRows({ total, shown, onShowAll, noun }) {
  if (total <= shown) return null;
  return (
    <button type="button" onClick={onShowAll} className="mx-4 mt-2 text-[12.5px] font-semibold text-brand-ink hover:underline sm:mx-5">
      Show all {formatNumber(total)} {noun}
    </button>
  );
}

const AedFigure = ({ value }) => (
  <>
    <span className="mr-1 text-[11px] font-bold text-ink-3">AED</span>
    {money(value)}
  </>
);

/**
 * props: projectId, data (useQbDesktopProject), canEdit (Invoicing edit level: Link / Change / Unlink), onChanged
 * (reload after a link change or its undo), invoices / variationInvoices / variations (to name the invoice here a
 * QuickBooks invoice matches).
 */
export function QbDesktopPanel({
  projectId, data, canEdit = false, onChanged, invoices = [], variationInvoices = [], variations = [],
}) {
  const [saving, setSaving] = useState(false);
  const [openSections, setOpenSections] = useState({ invoices: true });
  const [showAll, setShowAll] = useState({});
  const toggle = (key) => setOpenSections((current) => ({ ...current, [key]: !current[key] }));
  const rowsOf = (key, list) => (showAll[key] ? list : list.slice(0, ROW_LIMIT));
  const showAllOf = (key) => () => setShowAll((current) => ({ ...current, [key]: true }));

  // QuickBooks invoice → the invoice here it matches ("Matches VO-002 · 216")
  const hereByQbId = useMemo(() => {
    const voCode = new Map(variations.map((variation) => [variation.id, variation.vo_number || 'VO']));
    const platform = new Map([
      ...invoices.map((invoice) => [qbMatchKey(invoice.id, false), { code: '', reference: invoice.reference }]),
      ...variationInvoices.map((invoice) => [qbMatchKey(invoice.id, true), { code: voCode.get(invoice.variation_id) || 'VO', reference: invoice.reference }]),
    ]);
    const out = new Map();
    Object.entries(data?.matches || {}).forEach(([key, match]) => {
      if (!match?.qb_id || out.has(match.qb_id)) return;
      const here = platform.get(key);
      const name = here ? [here.code, here.reference].filter(Boolean).join(' · ') : '';
      out.set(match.qb_id, { match, label: name ? `Matches ${name}` : 'Matches an invoice here' });
    });
    return out;
  }, [data, invoices, variationInvoices, variations]);

  if (!data) return null;

  const changeLink = async (qbId, fullName = '') => {
    if (saving) return;
    setSaving(true);
    try {
      const result = await linkQbCustomer(projectId, qbId);
      onChanged?.();
      const undoId = result?.undo_id;
      const name = result?.linked?.full_name || fullName;
      notify.success(qbId ? `Linked to QuickBooks ${name}` : 'Unlinked from QuickBooks', {
        description: qbId ? 'Its QuickBooks invoices, payments and costs show on this tab — read only.' : undefined,
        ...(undoId ? {
          undo: async () => {
            // Undo exactly this change (a newer change by someone else is never rolled back).
            await api.post(`/undo/entries/${encodeURIComponent(undoId)}`);
            onChanged?.();
          },
          undoneMessage: qbId ? 'QuickBooks link undone' : 'QuickBooks link is back',
        } : {}),
      });
    } catch (err) {
      notify.error(qbId ? 'Could not link the project to QuickBooks' : 'Could not unlink the project from QuickBooks', { description: getErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const readAt = data.last_success_at;
  const meta = readAt ? <span title={formatDateTime(readAt)} data-testid="qbd-read-at">Read from QuickBooks {formatTimeAgo(readAt)}</span> : null;
  const linked = data.linked;

  if (!linked) {
    const suggestions = [...(data.suggestions || [])].sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0));
    return (
      <Panel className="overflow-hidden" data-testid="qbd-panel">
        <PanelHead
          title="QuickBooks"
          meta={meta}
          actions={canEdit ? (
            <CustomerPicker
              onPick={(qbId, fullName) => changeLink(qbId, fullName)}
              trigger={(
                <Button type="button" variant="ghost" size="sm" disabled={saving} data-testid="qbd-choose">
                  <Search aria-hidden="true" />
                  Choose…
                </Button>
              )}
            />
          ) : null}
        />
        <div className="px-4 py-3 sm:px-5" data-testid="qbd-unlinked">
          <p className="text-[13.5px] font-semibold text-ink">Link this project to its QuickBooks customer / job</p>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            Its QuickBooks invoices, payments and costs then show here next to the platform’s numbers. Read only — nothing is written to QuickBooks.
          </p>
          {suggestions.length ? (
            <ul className="mt-2.5 divide-y divide-line-2 rounded-xl border border-line" aria-label="Suggested QuickBooks customers and jobs">
              {suggestions.map((suggestion) => (
                <li key={suggestion.qb_id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2" data-testid={`qbd-suggestion-${suggestion.qb_id}`}>
                  <span className="inline-flex min-w-0 flex-1 basis-[180px] items-center gap-1.5 text-[13px] font-semibold text-ink">
                    <Link2 className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden="true" />
                    <span className="truncate">{suggestion.full_name}</span>
                  </span>
                  <span className="whitespace-nowrap text-[12px] text-ink-3">
                    {formatNumber(Math.round((Number(suggestion.score) || 0) * 100))}% alike by name
                    {suggestion.is_active === false ? ' · inactive' : ''}
                  </span>
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={saving}
                      onClick={() => changeLink(suggestion.qb_id, suggestion.full_name)}
                      aria-label={`Link to ${suggestion.full_name}`}
                      data-testid={`qbd-link-${suggestion.qb_id}`}
                    >
                      Link
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-[12.5px] text-ink-3">No QuickBooks customer or job looks like this project by name.</p>
          )}
          {!canEdit ? (
            <p className="mt-2 text-[12px] text-ink-3" data-testid="qbd-view-only">Someone who can edit Invoicing can link it.</p>
          ) : null}
        </div>
      </Panel>
    );
  }

  const totals = data.totals || {};
  const qbInvoices = data.invoices || [];
  const payments = data.payments || [];
  const costs = data.costs || {};
  const costLines = costs.lines || [];
  const vendors = costs.by_vendor || [];
  const openOrders = costs.open_purchase_orders || [];
  const costTotal = Number(costs.total ?? totals.costs ?? 0);
  const kinds = Object.entries(costs.by_kind || {}).filter(([, amount]) => Number(amount) !== 0);

  return (
    <Panel className="overflow-hidden" data-testid="qbd-panel">
      <PanelHead
        title={`QuickBooks · ${linked.full_name}`}
        meta={meta}
        actions={canEdit ? (
          <>
            <CustomerPicker
              onPick={(qbId, fullName) => changeLink(qbId, fullName)}
              trigger={(
                <Button type="button" variant="ghost" size="sm" disabled={saving} data-testid="qbd-change">
                  <ArrowLeftRight aria-hidden="true" />
                  Change
                </Button>
              )}
            />
            <Button type="button" variant="ghost" size="sm" disabled={saving} onClick={() => changeLink(null)} data-testid="qbd-unlink">
              <Unlink aria-hidden="true" />
              Unlink
            </Button>
          </>
        ) : null}
      />
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-3 sm:grid-cols-4 sm:px-5" data-testid="qbd-figures">
        <div data-testid="qbd-figure-invoiced"><MiniStat label="Invoiced" value={<AedFigure value={totals.invoiced} />} valueClassName="text-[17px]" /></div>
        <div data-testid="qbd-figure-received"><MiniStat label="Received" value={<AedFigure value={totals.received} />} valueClassName="text-[17px] text-status-green-ink" /></div>
        <div data-testid="qbd-figure-open"><MiniStat label="Open balance" value={<AedFigure value={totals.open_balance} />} valueClassName={cn('text-[17px]', Number(totals.open_balance) > 0 && 'text-status-amber-ink')} /></div>
        <div data-testid="qbd-figure-costs"><MiniStat label="Costs" value={<AedFigure value={totals.costs ?? costTotal} />} valueClassName="text-[17px]" /></div>
      </div>

      <Section id="invoices" title="QuickBooks invoices" count={qbInvoices.length} amount={totals.invoiced} open={Boolean(openSections.invoices)} onToggle={() => toggle('invoices')}>
        {qbInvoices.length ? (
          <>
            <MiniTable
              minWidth={620}
              columns={[{ label: 'Invoice' }, { label: 'Date' }, { label: 'Total', right: true }, { label: 'Balance', right: true }, { label: 'State' }, { label: 'Invoice here' }]}
            >
              {rowsOf('invoices', qbInvoices).map((invoice) => {
                const here = hereByQbId.get(invoice.qb_id);
                const balance = Number(invoice.balance_remaining || 0);
                return (
                  <tr key={invoice.qb_id || invoice.ref_number} data-testid={`qbd-invoice-${invoice.qb_id}`}>
                    <td className={cn(cell, MONO, 'whitespace-nowrap font-semibold text-ink')}>{invoice.ref_number || dash}</td>
                    <td className={cn(cell, 'whitespace-nowrap text-ink-2')}>{formatDate(invoice.txn_date)}</td>
                    <td className={cn(cell, 'text-right tabular-nums text-ink')}>{money(invoice.total ?? invoice.subtotal)}</td>
                    <td className={cn(cell, 'text-right tabular-nums', balance > 0 && !invoice.is_paid ? 'font-semibold text-status-amber-ink' : 'text-ink-2')}>{money(balance)}</td>
                    <td className={cell}>
                      {invoice.is_paid
                        ? <StatusPill tone="green" size="sm" label="Paid" />
                        : <StatusPill tone="amber" size="sm" label="Open" title={invoice.due_date ? `Due ${formatDate(invoice.due_date)}` : undefined} />}
                    </td>
                    <td className={cell}>
                      {here ? (
                        <StatusPill tone={qbMatchTone(here.match)} size="sm" label={here.label} title={qbMatchTitle(here.match)} />
                      ) : invoice.matched ? (
                        <StatusPill tone="green" size="sm" label="Matches an invoice here" />
                      ) : (
                        <StatusPill tone="amber" size="sm" label="Only in QuickBooks" title="No invoice of this project has this number" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </MiniTable>
            <MoreRows total={qbInvoices.length} shown={rowsOf('invoices', qbInvoices).length} onShowAll={showAllOf('invoices')} noun="invoices" />
          </>
        ) : <Empty>No QuickBooks invoice for this customer / job yet.</Empty>}
      </Section>

      <Section id="payments" title="Payments received" count={payments.length} amount={totals.received} open={Boolean(openSections.payments)} onToggle={() => toggle('payments')}>
        {payments.length ? (
          <>
            <MiniTable
              minWidth={560}
              columns={[{ label: 'Date' }, { label: 'Number' }, { label: 'Method' }, { label: 'Applied to' }, { label: 'Amount', right: true }]}
            >
              {rowsOf('payments', payments).map((payment, index) => {
                const applied = (payment.applied || []).map((entry) => (entry.ref_number ? `#${entry.ref_number}` : '')).filter(Boolean).join(', ');
                return (
                  <tr key={payment.qb_id || index} data-testid={`qbd-payment-${payment.qb_id || index}`}>
                    <td className={cn(cell, 'whitespace-nowrap text-ink-2')}>{formatDate(payment.txn_date)}</td>
                    <td className={cn(cell, MONO, 'whitespace-nowrap text-ink')}>{payment.ref_number || dash}</td>
                    <td className={cn(cell, 'text-ink-2')}>{payment.method || dash}</td>
                    <td className={cn(cell, 'max-w-[220px] truncate text-ink-2')} title={applied || undefined}>{applied || dash}</td>
                    <td className={cn(cell, 'text-right font-semibold tabular-nums text-ink')}>{money(payment.total)}</td>
                  </tr>
                );
              })}
            </MiniTable>
            <MoreRows total={payments.length} shown={rowsOf('payments', payments).length} onShowAll={showAllOf('payments')} noun="payments" />
          </>
        ) : <Empty>No payment received in QuickBooks for this customer / job yet.</Empty>}
      </Section>

      <Section id="costs" title="Costs" count={Number(costs.line_count ?? costLines.length)} amount={costTotal} open={Boolean(openSections.costs)} onToggle={() => toggle('costs')}>
        {costLines.length || vendors.length ? (
          <>
            {kinds.length ? (
              <p className="px-4 pb-2 text-[12px] text-ink-3 sm:px-5" data-testid="qbd-costs-by-kind">
                {kinds.map(([kind, amount]) => `${COST_KIND_PLURALS[kind] || kind} ${money(amount)}`).join(' · ')}
              </p>
            ) : null}
            {vendors.length ? (
              <>
                <h5 className="px-4 pb-1 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3 sm:px-5">By vendor</h5>
                <ul className="px-4 sm:px-5" data-testid="qbd-costs-vendors">
                  {rowsOf('vendors', vendors).map((vendor) => (
                    <li key={vendor.vendor} className="flex items-center gap-3 py-1 text-[12.5px]">
                      <span className="min-w-0 flex-1 truncate text-ink">{vendor.vendor}</span>
                      <ProgressBar
                        value={costTotal > 0 ? (Number(vendor.amount) / costTotal) * 100 : 0}
                        tone="navy"
                        className="w-[90px] shrink-0"
                        label={`${vendor.vendor} share of the costs`}
                      />
                      <span className="w-[110px] shrink-0 text-right font-semibold tabular-nums text-ink">{money(vendor.amount)}</span>
                    </li>
                  ))}
                </ul>
                <MoreRows total={vendors.length} shown={rowsOf('vendors', vendors).length} onShowAll={showAllOf('vendors')} noun="vendors" />
              </>
            ) : null}
            {costLines.length ? (
              <>
                <h5 className="px-4 pb-1 pt-3 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3 sm:px-5">
                  Latest lines
                  {Number(costs.line_count) > costLines.length ? <span className="font-semibold normal-case tracking-normal"> · the latest {formatNumber(costLines.length)} of {formatNumber(costs.line_count)}</span> : null}
                </h5>
                <MiniTable
                  minWidth={640}
                  columns={[{ label: 'Date' }, { label: 'Kind' }, { label: 'Vendor' }, { label: 'For' }, { label: 'Amount', right: true }]}
                >
                  {rowsOf('lines', costLines).map((line, index) => {
                    const what = [line.what, line.description].filter(Boolean).join(' — ');
                    return (
                      // a bill can have several lines with the same text: the position keeps the keys apart
                      <tr key={`${line.kind}-${line.ref_number}-${index}`} data-testid={`qbd-cost-line-${index}`}>
                        <td className={cn(cell, 'whitespace-nowrap text-ink-2')}>{formatDate(line.txn_date)}</td>
                        <td className={cn(cell, 'whitespace-nowrap text-ink-2')}>{COST_KIND_LABELS[line.kind] || line.kind}{line.ref_number ? <span className={cn(MONO, 'ml-1 text-ink-3')}>#{line.ref_number}</span> : null}</td>
                        <td className={cn(cell, 'max-w-[180px] truncate text-ink')} title={line.vendor || undefined}>{line.vendor || dash}</td>
                        <td className={cn(cell, 'max-w-[260px] truncate text-ink-2')} title={what || undefined}>{what || dash}</td>
                        <td className={cn(cell, 'text-right font-semibold tabular-nums text-ink')}>{money(line.amount)}</td>
                      </tr>
                    );
                  })}
                </MiniTable>
                <MoreRows total={costLines.length} shown={rowsOf('lines', costLines).length} onShowAll={showAllOf('lines')} noun="lines" />
              </>
            ) : null}
          </>
        ) : <Empty>No cost booked against this customer / job in QuickBooks yet.</Empty>}
      </Section>

      <Section
        id="orders"
        title="Open purchase orders"
        count={openOrders.length}
        amount={openOrders.reduce((sum, order) => sum + (Number(order.amount) || 0), 0)}
        open={Boolean(openSections.orders)}
        onToggle={() => toggle('orders')}
      >
        {openOrders.length ? (
          <>
            <MiniTable
              minWidth={560}
              columns={[{ label: 'PO' }, { label: 'Date' }, { label: 'Vendor' }, { label: 'Expected' }, { label: 'Amount', right: true }]}
            >
              {rowsOf('orders', openOrders).map((order, index) => (
                <tr key={order.qb_id || `${order.ref_number}-${index}`} data-testid={`qbd-order-${order.ref_number || index}`}>
                  <td className={cn(cell, MONO, 'whitespace-nowrap font-semibold text-ink')}>{order.ref_number || dash}</td>
                  <td className={cn(cell, 'whitespace-nowrap text-ink-2')}>{formatDate(order.txn_date)}</td>
                  <td className={cn(cell, 'max-w-[220px] truncate text-ink')} title={order.vendor || undefined}>{order.vendor || dash}</td>
                  <td className={cn(cell, 'whitespace-nowrap text-ink-2')}>{order.expected_date ? formatDate(order.expected_date) : dash}</td>
                  <td className={cn(cell, 'text-right font-semibold tabular-nums text-ink')}>{money(order.amount)}</td>
                </tr>
              ))}
            </MiniTable>
            <MoreRows total={openOrders.length} shown={rowsOf('orders', openOrders).length} onShowAll={showAllOf('orders')} noun="purchase orders" />
          </>
        ) : <Empty>No open purchase order for this customer / job in QuickBooks.</Empty>}
      </Section>
    </Panel>
  );
}
